import styles from "./OrderSettingsForm.module.css";
import React, { useEffect, useState, useRef, useMemo } from "react";
import SubmitButton from "../../../../components/Button/SubmitButton/SubmitButton";
import UserInput from "../../../../components/Input/UserInput/UserInput";
import AddressInput from "../../../../components/Input/AddressInput/AddressInput";
import DatePicker from "../../../../components/DatePicker/DatePicker";
import { useNavigate } from "react-router-dom";
import { useOrderContext } from "../../../../context/OrderContext";
import { useDontShowOnboarding } from "../../../../hooks/useDontShowOnboarding";
import { useUserContext } from "../../../../context/UserContext";
import { useTranslation } from "react-i18next";
import { createCart } from "../../../../services/cartService";
import { useCartContext } from "../../../../context/CartContext";
import { normalizeDate, slashDMYtoISO, filterDatesOnOrAfterEffectiveTomorrow } from "../../../../utils/calendar";
import { log, error as logError } from "../../../../utils/logger";

const isISODate = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s);

// inclusive range: 2025-12-25..2026-01-11
const buildIsoDateRange = (fromISO: string, toISO: string): string[] => {
  if (!isISODate(fromISO) || !isISODate(toISO)) return [];
  const out: string[] = [];

  const start = new Date(`${fromISO}T00:00:00`);
  const end = new Date(`${toISO}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];

  const dir = start <= end ? 1 : -1;
  const d = new Date(start);

  while ((dir === 1 && d <= end) || (dir === -1 && d >= end)) {
    const iso = d.toISOString().slice(0, 10);
    out.push(iso);
    d.setDate(d.getDate() + dir);
  }

  return out;
};

const OrderSettingsForm = () => {
  const { t } = useTranslation();
  const { handleOrder } = useOrderContext();
  const { userData, loggedIn } = useUserContext();
  const { dontShow } = useDontShowOnboarding();
  const navigate = useNavigate();

  const [user, setUser] = useState<number>();
  const [address, setAddress] = useState("");
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [creating, setCreating] = useState<boolean>(false);

  const { cartData, cartLoaded, reloadCart } = useCartContext();

  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [adresses, setAdresses] = useState<string[]>([]);

  // We still only prefill once per mount,
  // BUT we override existing form values when cart is active.
  const didPrefillFromCartRef = useRef(false);

  // Ensure cart context is loaded (optional but helps)
  useEffect(() => {
    if (loggedIn && !cartLoaded) {
      reloadCart().catch(() => {});
    }
  }, [loggedIn, cartLoaded, reloadCart]);

  // Derive cart dates in ISO from: selected_dates -> items keys -> time.from/to
  const cartDatesISO = useMemo(() => {
    if (!cartData) return [];

    // 1) selected_dates if present
    const rawSelected = cartData.selected_dates;
    if (Array.isArray(rawSelected) && rawSelected.length > 0) {
      return filterDatesOnOrAfterEffectiveTomorrow(
        rawSelected
        .map((d: string) => normalizeDate(d))
        .filter((d): d is string => Boolean(d) && isISODate(d))
      );
    }

    // 2) items keys (your cart has: "25. 12. 2025": [...])
    const keys = cartData.items ? Object.keys(cartData.items) : [];
    const fromKeys = keys
      .map((k) => normalizeDate(k))
      .filter((d): d is string => Boolean(d) && isISODate(d));
    if (fromKeys.length > 0) return filterDatesOnOrAfterEffectiveTomorrow(fromKeys);

    // 3) time range (your cart has from/to)
    const from = cartData.time?.from;
    const to = cartData.time?.to;
    if (from && to) {
      const fromISO = slashDMYtoISO(from); // "25/12/2025" -> "2025-12-25"
      const toISO = slashDMYtoISO(to);
      return filterDatesOnOrAfterEffectiveTomorrow(buildIsoDateRange(fromISO, toISO));
    }

    return [];
  }, [cartData]);

  // If ONLY one subscriber exists, set default user (BUT cart prefill will override later)
  useEffect(() => {
    if (!user && userData.subscribers?.length === 1) {
      setUser(userData.subscribers[0].subscriber_id);
    }
  }, [userData, user]);

  // Prefill from ACTIVE cart (override existing filled values)
  useEffect(() => {
    if (!cartLoaded || !cartData) return;
    if (didPrefillFromCartRef.current) return;

    if (cartData.cart_active !== true) return;

    didPrefillFromCartRef.current = true;

    // subscriber
    if (typeof cartData.subscriber_id === "number") {
      setUser(cartData.subscriber_id);
    }

    // address (only if cart actually has it)
    const cartAddress = (cartData as any).delivery_adress?.adress ?? "";
    if (typeof cartAddress === "string" && cartAddress.trim()) {
      setAddress(cartAddress);
    }

    // dates (from derived cartDatesISO)
    if (cartDatesISO.length > 0) {
      setSelectedDates(cartDatesISO);
    }
  }, [cartLoaded, cartData, cartDatesISO]);

  // Drop dates that are no longer valid after cutoff
  useEffect(() => {
    if (selectedDates.length === 0) return;
    const filtered = filterDatesOnOrAfterEffectiveTomorrow(selectedDates);
    if (filtered.length !== selectedDates.length) {
      setSelectedDates(filtered);
    }
  }, [selectedDates]);

  // When subscriber changes -> load their addresses
  useEffect(() => {
    const tempAdresses =
      userData.subscribers
        ?.filter((sub) => sub.subscriber_id === user)
        .flatMap((s) => s.delivery_adresses.map((a) => a.adress)) ?? [];

    setAdresses(tempAdresses);

    // keep your old behavior: if exactly one address and address empty, auto-fill
    if (tempAdresses.length === 1 && !address.trim()) {
      setAddress(tempAdresses[0]);
    }
  }, [userData, user, address]);

  const validate = () => {
    const next: Record<string, boolean> = {};
    if (!user) next.user = true;
    if (!address.trim()) next.address = true;
    if (selectedDates.length === 0) next.dates = true;
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const continueForm = (event: React.FormEvent) => {
    event.preventDefault();
    if (!validate()) return;
    handleSubmit();
  };

  const handleSubmit = async () => {
    if (
      user !== undefined &&
      address.trim() !== "" &&
      selectedDates.length !== 0 &&
      cartLoaded &&
      loggedIn
    ) {
      handleOrder("address", address);
      handleOrder("recipient", user);
      handleOrder("date", selectedDates); 
      

      try {
        setCreating(true);

        const formDates = selectedDates.map((d) =>
          new Date(d).toLocaleDateString("cs")
        );
        /*
        log( user,
          selectedDates[0],
          selectedDates[selectedDates.length - 1],
          address,
          formDates)
          */
        await createCart(
          user,
          selectedDates[0],
          selectedDates[selectedDates.length - 1],
          address,
          formDates
        );

        await reloadCart();
      } catch (error) {
        logError("Error creating cart:", error);
      } finally {
        setCreating(false);
      }

      navigate(dontShow === true ? "/home" : "/onboarding");
    }
  };

  return (
    <>
      <form action="" className={styles.Form}>
        <div className={styles.InputCapsule}>
          <div className={styles.InputBlock}>
            <div className={styles.InputLabel}>
              <p>{t("order_settings.inputs.customer.label")}</p>
            </div>

            <UserInput
              onChange={setUser}
              value={user}
              className={errors.user ? styles.Missing : ""}
              users={
                userData.subscribers && userData.subscribers.length !== 0
                  ? userData.subscribers
                  : null
              }
            />

            {errors.user && (
              <span id="user-error" className={styles.ErrorText}>
                {t("errors.user")}
              </span>
            )}
            <div className={styles.InputTooltip}></div>
          </div>

          <div className={styles.InputBlock}>
            <div className={styles.InputLabel}>
              <p>{t("order_settings.inputs.address.label")}</p>
            </div>

            <AddressInput
              className={errors.address ? styles.Missing : ""}
              onChange={setAddress}
              value={address}
              addresses={adresses}
            />

            {errors.address && (
              <span id="address-error" className={styles.ErrorText}>
                {t("errors.address")}
              </span>
            )}
            <div className={styles.InputTooltip}></div>
          </div>

          <div className={styles.InputBlock}>
            <div className={styles.InputLabel}>
              <p>{t("order_settings.inputs.date.label")}</p>
            </div>

            <DatePicker
              className={errors.dates ? styles.Missing : ""}
              onChange={setSelectedDates}
              selectedDates={selectedDates}
            />

            {errors.dates && (
              <span id="dates-error" className={styles.ErrorText}>
                {t("errors.dates")}
              </span>
            )}
            <div className={styles.InputTooltip}></div>
          </div>
        </div>

        <SubmitButton
          text={t("buttons.proceed")}
          onClick={(e) => continueForm(e)}
          disabled={creating === true}
        />
      </form>
    </>
  );
};

export default OrderSettingsForm;
